"use client";
import { useMemo, useState, useEffect } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { MagnifyingGlassIcon, StarIcon } from "@heroicons/react/24/solid";
import { StarIcon as StarOutline } from "@heroicons/react/24/outline";
import Image from "next/image";
import { getApiBaseUrl } from "@/lib/apiBase";
import { fetchJsonArray } from "@/lib/fetchJsonSafe";
const BASE_URL = getApiBaseUrl();

function buildSafeImageUrl(raw) {
  if (!raw || typeof raw !== "string") return null;
  const value = raw.trim();
  if (!value || value === "null" || value === "undefined") return null;
  if (/^https?:\/\//i.test(value)) return value;
  if (/^data:/i.test(value)) return value;
  const path = value.startsWith("/") ? value : `/${value}`;
  return BASE_URL ? `${BASE_URL}${path}` : path;
}

function shouldUnoptimizeImage(src) {
  return typeof src === "string" && /^data:/i.test(src);
}

function StarRating({ value, size = "w-5 h-5" }) {
  const v = Math.round(value * 2) / 2;
  const full = Math.floor(v);
  const half = v % 1 !== 0;

  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => {
        if (i < full) {
          return <StarIcon key={i} className={`${size} primary-color`} />;
        }
        if (i === full && half) {
          return (
            <div key={i} className="relative">
              <StarOutline className={`${size} primary-color`} />
              <StarIcon
                className={`${size} primary-color absolute inset-0`}
                style={{ clipPath: "inset(0 50% 0 0)" }}
              />
            </div>
          );
        }
        return <StarOutline key={i} className={`${size} text-gray-300`} />;
      })}
    </div>
  );
}

function ResultCard({ company }) {
  const router = useRouter();
  const companyImageUrl = buildSafeImageUrl(company?.image);
  const averageRating =
    company.reviews.length > 0
      ? (
          company.reviews.reduce((sum, r) => sum + r.rating, 0) /
          company.reviews.length
        ).toFixed(1)
      : "N/A";

  const isVIP = company.is_vip || company.vip || company.isVip || false;
  const hasActivePackage = company.packages && company.packages.length > 0 && company.packages.some(pkg => pkg.is_active);
  const isPremium = isVIP || hasActivePackage;

  return (
    <div
      className={`flex items-center gap-4 p-4 bg-white border-b hover:bg-gray-100 transition-all duration-200 ${
        isPremium 
          ? "border-l-4 border-l-yellow-400 bg-yellow-50/30" 
          : "border-gray-100"
      }`}
    >
      <div 
        className="flex items-center gap-4 flex-1 min-w-0 cursor-pointer"
        onClick={() => router.push(`/partner/${company.id}`)}
      >
        <div className="relative w-[80px] h-[80px] overflow-hidden rounded-lg flex-shrink-0">
          {companyImageUrl ? (
            <Image
              src={companyImageUrl}
              alt={company.company_name}
              fill
              className="object-cover transition-transform duration-700 ease-out group-hover:scale-110"
              unoptimized={shouldUnoptimizeImage(companyImageUrl)}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-slate-100 text-xl font-semibold text-slate-400">
              {company.company_name?.[0]?.toUpperCase() || "C"}
            </div>
          )}
          {isVIP && (
            <div className="absolute top-1 right-1 bg-gradient-to-r from-yellow-400 to-yellow-500 text-white rounded-full px-1.5 py-0.5 text-[10px] font-bold shadow-lg border border-white z-10">
              ⭐ VIP
            </div>
          )}
          {!isVIP && hasActivePackage && (
            <div className="absolute top-1 right-1 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-full px-1.5 py-0.5 text-[10px] font-bold shadow-lg border border-white z-10">
              📦 {company.packages[0]?.package?.name || 'Pkg'}
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0 space-y-1">
          <div className="flex items-center gap-2">
            <h3 className={`font-semibold text-base truncate hover:text-[#577099] transition-colors ${
              isPremium 
                ? "text-yellow-600" 
                : "text-gray-900"
            }`}>
              {company.company_name}
            </h3>
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-600">
            {averageRating !== "N/A" ? (
              <>
                <span>⭐ {averageRating}</span>
                <span>· {company.reviews.length} reviews</span>
              </>
            ) : (
              <span>No reviews yet</span>
            )}
          </div>
          <div className="mt-1 text-xs text-gray-700 space-y-0.5">
            {company.company_email && (
              <div className="truncate">
                <b>Имэйл:</b> {company.company_email}
              </div>
            )}
            {company.company_phone && (
              <div className="truncate">
                <b>Утас:</b> {company.company_phone}
              </div>
            )}
            {company.address && (
              <div className="truncate">
                <b>Хаяг:</b> {company.address}
              </div>
            )}
            {company.category?.name && (
              <div className="truncate">
                <b>Ангилал:</b> {company.category?.name}
              </div>
            )}
          </div>
        </div>
      </div>
      <Link
        href={`/review/${company.id}`}
        className="flex-shrink-0 text-sm font-semibold text-center text-[#577099] hover:text-[#4a5f85] transition-colors whitespace-nowrap px-3 py-2 bg-[#577099]/5 hover:bg-[#577099]/10 rounded-lg"
        onClick={(e) => e.stopPropagation()}
      >
        Сэтгэгдэл<br />бичих
      </Link>
    </div>
  );
}

function averageRatingOf(company) {
  const reviews = Array.isArray(company?.reviews) ? company.reviews : [];
  const ratings = reviews
    .map((r) => Number(r?.rating))
    .filter((n) => Number.isFinite(n) && n > 0);
  if (ratings.length === 0) return 0;
  return ratings.reduce((sum, n) => sum + n, 0) / ratings.length;
}


export default function FindReviewPage() {
  const [query, setQuery] = useState("");
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        setLoading(true);
        const data = await fetchJsonArray(`/api/partner?type=partner`);
        const list = Array.isArray(data) ? data : [];
        const sorted = [...list].sort((a, b) => {
          const aVip = !!(a?.is_vip || a?.vip || a?.isVip);
          const bVip = !!(b?.is_vip || b?.vip || b?.isVip);
          const aPkg = Array.isArray(a?.packages) && a.packages.some((pkg) => pkg?.is_active);
          const bPkg = Array.isArray(b?.packages) && b.packages.some((pkg) => pkg?.is_active);
          const aPremium = aVip || aPkg;
          const bPremium = bVip || bPkg;
          if (aPremium !== bPremium) return aPremium ? -1 : 1;
          const aRating = averageRatingOf(a);
          const bRating = averageRatingOf(b);
          if (bRating !== aRating) return bRating - aRating;
          const aCount = Array.isArray(a?.reviews) ? a.reviews.length : 0;
          const bCount = Array.isArray(b?.reviews) ? b.reviews.length : 0;
          if (bCount !== aCount) return bCount - aCount;
          return (a?.company_name || "").localeCompare(b?.company_name || "");
        });
        setCompanies(sorted);
      } catch (error) {
        console.error("Error fetching companies:", error);
        setCompanies([]);
      } finally {
        setLoading(false);
      }
    };
    fetchCompanies();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      // Query байхгүй үед VIP-үүд түрүүнд эрэмбэлэгдсэн эхний 10-ыг харуулна
      return companies.slice(0, 10);
    }
    return companies.filter((c) => c.company_name.toLowerCase().includes(q));
  }, [query, companies]);

  return (
    <div className="bg-slate-50 flex justify-center py-8" style={{ paddingTop: '20vh', minHeight: 'auto' }}>
      <div className="max-w-3xl w-full mx-auto px-6">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center leading-tight mb-12" style={{ background: 'linear-gradient(to right, #1f2837, #577099, #1f2837)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
          Өөрийн туршлагаа бусадтай үргэлж хуваалцая
        </h1>

        <div className="relative" style={{ marginBottom: '20vh' }} ref={(el) => {
          if (el && !el._dropdownRef) {
            el._dropdownRef = el;
          }
        }}>
          <MagnifyingGlassIcon className="w-6 h-6 absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 z-10" />
          <input
            type="text"
            placeholder="Хайгаад үзээд сэтгэгдлээ үлдээгээрэй..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={(e) => {
              // Delay to allow click events on dropdown items to register
              setTimeout(() => {
                if (!e.relatedTarget || !e.relatedTarget.closest('.absolute')) {
                  setIsFocused(false);
                }
              }, 200);
            }}
            className="w-full pl-14 pr-4 py-5 text-lg rounded-full shadow-sm focus:outline-none focus:ring-2 focus:ring-[#577099] bg-white"
          />

        {isFocused && loading && (
          <div className="max-h-96 overflow-y-auto rounded-2xl pt-2">
            <div className="space-y-4">
              {[...Array(5)].map((_, index) => (
                <div key={index} className="animate-pulse p-5 bg-white rounded-2xl border border-gray-200 shadow-sm">
                  <div className="flex items-center gap-4">
                    <div className="w-20 h-20 bg-gray-200 rounded-lg"></div>
                    <div className="flex-1 space-y-2">
                      <div className="h-6 bg-gray-200 rounded w-3/4"></div>
                      <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                      <div className="h-4 bg-gray-200 rounded w-full"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {isFocused && !loading && (
          <div className="absolute top-full left-0 w-full mt-2 z-50">
            <div className="bg-white rounded-lg shadow-xl overflow-hidden border border-gray-200">
              <div className="max-h-80 overflow-y-auto overflow-x-hidden">
                {filtered.length > 0 ? (
                  filtered.map((c) => <ResultCard key={c.id} company={c} />)
                ) : (
                  <div className="p-6 text-center text-gray-500 text-sm">
                    <p className="mb-2">Одоогоор хайлтанд тохирох компаниуд олдсонгүй.</p>
                  </div>
                )}
              </div>
              {filtered.length > 0 && (
                <button
                  type="button"
                  style={{
                    background: 'linear-gradient(to right, #1f2837, #2d3a4a, #1f2837)',
                  }}
                  className="w-full px-4 py-3 text-sm font-semibold text-white hover:opacity-90 transition-all duration-300 border-t border-gray-200"
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(to right, #577099, #4a5f85, #577099)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(to right, #1f2837, #2d3a4a, #1f2837)';
                  }}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => window.location.href = `/partner/partnersearch?search=${query || ''}`}
                >
                  Бүгдийг харах
                </button>
              )}
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
}
